import React, { Fragment, useState } from 'react';
import { graphql } from 'gatsby';
import PropTypes from 'prop-types';
import 'url-search-params-polyfill';
import {
  PageHeader,
  SimpleDataTable,
  Section,
  toSectionsURL,
  makeBreadcrumb,
  GatsbyLink,
  TabContainer,
  Tab,
  slugify,
  getObjByValue,
  findObjIndexByKey
} from '@budget-monorepo/shared-ui';

import Layout from '../components/Layout';
import HelmetData from '../components/HelmetData';
import PlanFilter from '../components/PlanFilter';

const GovLevelTwo = (props) => {
  const { data, location } = props;
  const { content, site, tabsNav } = data;
  const tabs = tabsNav.navItems;
  const {
    title, budgetSection, summaryTable, govLevelOne, govLevelTwo, parent: { parent }
  } = content;
  // eslint-disable-next-line camelcase
  const { modifiedTime, mg_mod_date, mg_mod_datetime } = parent;
  const path = location.pathname;
  const pathPrefix = process.env.GATSBY_PATH_PREFIX;
  const relPath = path.replace(pathPrefix, '');
  const { pageTitle, breadcrumbprops } = makeBreadcrumb({
    href: '/beneficiary-agency',
    text: 'Plan by Beneficiary Agency',
    sections: [govLevelOne, govLevelTwo]
  });
  const layoutProps = {
    activeIndex: 1,
    pre: () => <PlanFilter path={relPath} />,
    above: () => <PageHeader title={title} category={budgetSection} />,
    pageTitle,
    breadcrumbprops,
    dataTimeStamp: modifiedTime,
    buildTimeStamp: site.buildTime,
    containerClasses: 'main-content main-content--two'
  };
  const { headers, headersBySource } = summaryTable;
  const createTableData = (tabData, dataKey) => {
    const h = (dataKey === 'bySourceTab') ? headersBySource : headers;
    const tableData = tabData.data.map((item) => {
      // Suppress plan item with "suppressed" flag
      if (item.suppressed) {
        return 0;
      }
      const overrideKey = 'planItemNumber';
      const text = item.planItemNumber;
      const href = toSectionsURL({
        href: '/beneficiary-agency/',
        sections: [item.govLevelOne, item.govLevelTwo, item.planItemNumber]
      });
      const newRowData = Object.assign({}, item, {
        [overrideKey]: <GatsbyLink to={href}>{text}</GatsbyLink>
      });
      return newRowData
    })
    const tableProps = { data: tableData, total: tabData.total, headers: h };
    return tableProps;
  }

  const urlToTabIndex = (location) => {
    let index = 0;
    if (!location.href) {
      return null;
    }
    const url = new URL(location.href);
    const params = new URLSearchParams(url.search);
    const tabParam = params.get('tab');
    if (tabParam) {
      // Tab text set in tabs.json
      const tabKey = 'href'; 
      const tabData = tabs;
      index = findObjIndexByKey({ arr: tabData, value:tabParam, key: tabKey, slug: true });
    }
    return index;
  };

  const urlToSubTabIndex = (location) => {
    let index = 0;
    if (!location.href) {
      return null;
    }
    const url = new URL(location.href);
    const params = new URLSearchParams(url.search);
    const tabParam = params.get('tab');
    const subTabParam = params.get('subtab');
    if (tabParam && subTabParam) {
      // Tab text set in tabs.json
      const tabKey = 'href'; 
      const subTabKey = 'name';
      const parentTab = getObjByValue(tabs, tabParam, tabKey);
      const subTabData = summaryTable[parentTab.dataKey];
      index = findObjIndexByKey({ arr: subTabData, value: subTabParam, key: subTabKey, slug: true});
    }
    return index;
  };

  const [ tabIndex, setTabIndex ] = useState(urlToTabIndex(location));
  const [ subTabIndex, setSubTabIndex ] = useState(urlToSubTabIndex(location));

  const onTabChange = (tabRef, curLocation) => {
    const { location: newLocation } = props;
    if (global.window && curLocation.href) {
      const tab = tabRef.current.innerText;
      const { pathname } = newLocation;
      const url = `${pathname}?tab=${slugify(tab)}`;
      // eslint-disable-next-line no-undef
      window.history.replaceState({}, '', url);
      const index = findObjIndexByKey({ arr: tabs, value: tab, key: 'text'});
      setTabIndex(index);
      // reset sub tab on tab change
      setSubTabIndex(0);
    }
  };

  const onSubTabChange = ({tabRef, location: curLocation, parentTab}) => {
    const { location } = props;
    if (global.window && curLocation.href && tabRef.current) {
      const subTab = tabRef.current.innerText;
      const { pathname } = location;
      const url = `${pathname}?tab=${parentTab.href}&subtab=${slugify(subTab)}`;
      // eslint-disable-next-line no-undef
      window.history.replaceState({}, '', url);
      const index = findObjIndexByKey({ arr: summaryTable[parentTab.dataKey], value: subTab, key: 'name'});
      setSubTabIndex(index);
    }
  };


  // metadata for the page
  const metaTitle = title;
  const metaDescription = `Capital investment plan for beneficiary state organization - ${title}.`;
  return(
    <Fragment>
      <HelmetData {...{
        metaTitle,
        metaDescription,
        mg_mod_date,
        mg_mod_datetime
      }}
      />
      <Layout {...layoutProps}>
        {summaryTable
          && (
          <Section>
              <TabContainer
                defaultTab={tabIndex}
                onTabChange={(tabRef) => onTabChange(tabRef, location)}              
              >
                {
                  tabs.map((tab) => (
                    <Tab 
                      title={tab.text}
                      key={`tab-${tab.href}`}
                  >
                      <TabContainer
                        nested
                        defaultTab={subTabIndex}
                        onTabChange={(tabRef) => onSubTabChange({ tabRef, location, parentTab: tab })}
                      >
                        {
                          summaryTable[tab.dataKey].map((subTab) => (
                            <Tab
                              title={subTab.name}
                              key={`subtab-${subTab.value}`}
                            >
                            <SimpleDataTable title={`${title} ${budgetSection}`} table={createTableData(subTab, tab.dataKey)} maxColWidth={200} download={`${location.pathname}/${subTab.name}`} />
                            </Tab>
                          ))
                        }
                      </TabContainer>
                    </Tab>
                  ))
                }
              </TabContainer>
          </Section>
          )
        }
      </Layout>
    </Fragment>
  );
};

GovLevelTwo.propTypes = {
  data: PropTypes.object,
  pageContext: PropTypes.object,
  location: PropTypes.object
};

export default GovLevelTwo;

export const query = graphql`
  query($slug: String!) {
    site {
      ...SiteMetadata
    }
    tabsNav: navJson(navType: {eq: "tabs"}) {
      ...SiteNav
    }
    content: planGovLevelTwo(fields: { slug: { eq: $slug } }) {
      title
      budgetSection
      govLevelOne
      govLevelTwo
      fields {
        slug
      }
      summaryTable {
        headers {
          name
          type
          align
          value
        }
        headersBySource {
          name
          type
          align
          value
        }
        byYearTab {
          name
          value
          data {
            suppressed
            planItemNumber
            planItemName
            govLevelOne
            govLevelTwo
            stateBondCap
            nonBondCap
            operatingFunds
            federalFunds
            otherFunds
            total
          }
          total {
            govLevelTwo
            stateBondCap
            nonBondCap
            operatingFunds
            federalFunds
            otherFunds
            total
          }
        }
        bySourceTab {
          name
          value
          data {
            fy1
            fy2
            fy3
            fy4
            fy5
            fy6
            govLevelOne
            govLevelTwo
            planItemName
            planItemNumber
            source
            suppressed
          }
          total {
            fy1
            fy3
            fy2
            fy4
            fy5
            fy6
            govLevelTwo
            govLevelTwoReal
            source
          }
        }
      }
      # Get the last modified timestamp for the file powering the plan item pages
      ...PlanGovLevelTwoDataTimeStamp
    }
  }
`;
